import React from 'react';
import { content } from '../data/mock';
import { useLanguage } from '../context/LanguageContext';
import { MapPin, ArrowRight, Plane } from 'lucide-react';

const Destinations = () => {
  const { language } = useLanguage();
  const t = content[language]?.destinations || content.en.destinations;

  return (
    <section id="destinations" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-2 rounded-full bg-cyan-50 text-cyan-600 font-medium text-sm mb-4">
            {t.subtitle}
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 mb-4">
            {t.title}
          </h2>
          <p className="text-slate-600 max-w-2xl mx-auto text-lg">
            {t.description}
          </p>
        </div>

        {/* Map visualization */}
        <div className="relative bg-gradient-to-br from-slate-100 to-slate-50 rounded-3xl p-8 md:p-12 mb-12 overflow-hidden">
          {/* Decorative plane path */}
          <div className="absolute inset-0 flex items-center justify-center opacity-10">
            <svg className="w-full h-full" viewBox="0 0 800 400" preserveAspectRatio="xMidYMid slice">
              <path 
                d="M100 200 Q400 50 700 200" 
                stroke="currentColor" 
                strokeWidth="2" 
                fill="none" 
                strokeDasharray="10,10"
                className="text-blue-500"
              />
              <Plane x="380" y="100" className="w-8 h-8 text-blue-500 transform -rotate-45" />
            </svg>
          </div>

          {/* Locations grid */}
          <div className="relative grid md:grid-cols-2 lg:grid-cols-5 gap-6">
            {t.locations.map((location, index) => (
              <div
                key={index}
                className={`group relative p-6 rounded-2xl bg-white shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 ${
                  index === 0 ? 'lg:col-span-1 ring-2 ring-blue-500' : ''
                }`}
              >
                {/* Flag */}
                <div className="text-4xl mb-3">
                  {location.flag}
                </div>
                
                {/* Country */}
                <h3 className="text-lg font-bold text-slate-900 mb-2">
                  {location.country}
                </h3>
                
                {/* Cities */}
                <div className="space-y-1">
                  {location.cities.map((city, cityIndex) => (
                    <div key={cityIndex} className="flex items-center gap-2 text-slate-600 text-sm">
                      <MapPin className="w-3 h-3 text-orange-500" />
                      {city}
                    </div>
                  ))}
                </div>

                {/* HQ badge for Canada */}
                {index === 0 && (
                  <div className="absolute -top-2 -right-2 bg-blue-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                    HQ
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Connection routes */}
        <div className="grid md:grid-cols-2 gap-6">
          <div className="bg-gradient-to-r from-blue-600 to-cyan-600 rounded-2xl p-6 text-white">
            <div className="flex items-center gap-4 mb-4">
              <div className="text-3xl">🇨🇦</div>
              <ArrowRight className="w-6 h-6" />
              <div className="text-3xl">🌍</div>
            </div>
            <h3 className="text-xl font-bold mb-2">
              {language === 'fr' ? 'Du Canada vers l\'Afrique' : 'From Canada to Africa'}
            </h3>
            <p className="text-white/80">
              {language === 'fr' 
                ? 'Livraison express en 3-7 jours vers toutes nos destinations africaines.'
                : 'Express delivery in 3-7 days to all our African destinations.'}
            </p>
          </div>

          <div className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-2xl p-6 text-white">
            <div className="flex items-center gap-4 mb-4">
              <div className="text-3xl">🌍</div>
              <ArrowRight className="w-6 h-6" />
              <div className="text-3xl">🇨🇦</div>
            </div>
            <h3 className="text-xl font-bold mb-2">
              {language === 'fr' ? 'De l\'Afrique vers le Canada' : 'From Africa to Canada'}
            </h3>
            <p className="text-white/80">
              {language === 'fr' 
                ? 'Service de retour disponible avec ramassage à domicile en 4-10 jours.'
                : 'Return service available with home pickup in 4-10 days.'}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Destinations;
